/**
 * Browser storage holding a signed-in user's data, cleared on an explicit
 * logout (#2272) so the next person at the browser does not inherit it.
 * NOT cleared when a timed-out session is resumed by signing in again: the
 * brewDB workbench's resume cache is meant to survive that.
 *
 *   localStorage   `brewlytics:brewdb-workbench:<userId>` (and the legacy
 *                  shared `brewlytics:brewdb-workbench`): open query tabs,
 *                  including their SQL (store/brewdbStore.ts).
 *   localStorage   `brewlytics:discover-layout:<profileId>`: the last Discover
 *                  layout, with the profile's private saved searches (titles,
 *                  descriptions, queries) (DiscoverVariants/saved/discoverLayoutStore.ts).
 *   sessionStorage `brewlytics:brew-ai-code:<modelId>:<functionalId>`: brew-ai
 *                  chat transcripts, keyed by model, not by user
 *                  (store/brewAiStore.ts).
 *
 * Device preferences (panel widths, map base layer, run-view choice, ...) are
 * not user data and stay. oidc-client-ts removes its own `oidc.*` records.
 */
const LOCAL_PREFIXES = ['brewlytics:brewdb-workbench', 'brewlytics:discover-layout:'];
const SESSION_PREFIXES = ['brewlytics:brew-ai-code:'];

function removeByPrefix(storage: () => Storage, prefixes: string[]): void {
  try {
    const store = storage();
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) doomed.push(key);
    }
    for (const key of doomed) store.removeItem(key);
  } catch {
    // storage unavailable (private mode, blocked site data): nothing to clear
  }
}

let clears = 0;

export function clearUserStorage(): void {
  clears += 1;
  removeByPrefix(() => window.localStorage, LOCAL_PREFIXES);
  removeByPrefix(() => window.sessionStorage, SESSION_PREFIXES);
}

/** How many times this page has cleared the user's storage. Work started
 *  before a sign-out compares it to stop sending or storing that user's
 *  data afterwards (brewDB autosave, #2400). */
export function userStorageClears(): number {
  return clears;
}
